import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Snackbar,
  Typography,
} from "@mui/material";
import { ArrowDown, ArrowUp, Check, X } from "lucide-react";
import { useEffect, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import Button from "../../common/components/Button";
import PageHeader from "../../common/components/PageHeader";
import { SPACING } from "../../common/constants/spacing";
import { useAuth } from "../../common/contexts/AuthContext";
import { createCheckoutSession, verifySession } from "../api/payments";
import { changeSubscriptionPlan } from "../api/subscription";

function MembershipPage() {
  const { t } = useTranslation("membership");
  const { subscriptionPlan, setSubscriptionPlan } = useAuth();
  const [upgradeDialog, setUpgradeDialog] = useState(false);
  const [cancelDialog, setCancelDialog] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [currentPlan, setCurrentPlan] = useState(subscriptionPlan || "free");

  // Helper function to determine plan hierarchy level
  const getPlanLevel = (planName) => {
    const levels = {
      free: 0,
      discovery: 1,
      professional: 2,
      premium: 3,
      enterprise: 4,
    };
    return levels[planName] || 0;
  };

  // Helper function to ensure value is an array
  const ensureArray = (value) => {
    return Array.isArray(value) ? value : [];
  };

  const plans = [
    {
      name: "free",
      displayName: t("plans.free.name"),
      price: t("plans.free.price"),
      period: t("plans.free.period"),
      description: t("plans.free.description"),
      features: ensureArray(t("plans.free.features", { returnObjects: true })),
      limitations: ensureArray(
        t("plans.free.limitations", { returnObjects: true }),
      ),
      popular: false,
    },
    {
      name: "discovery",
      displayName: t("plans.discovery.name"),
      price: t("plans.discovery.price"),
      period: t("plans.discovery.period"),
      description: t("plans.discovery.description"),
      features: ensureArray(
        t("plans.discovery.features", { returnObjects: true }),
      ),
      limitations: ensureArray(
        t("plans.discovery.limitations", { returnObjects: true }),
      ),
      popular: false,
    },
    {
      name: "professional",
      displayName: t("plans.professional.name"),
      price: t("plans.professional.price"),
      period: t("plans.professional.period"),
      description: t("plans.professional.description"),
      features: ensureArray(
        t("plans.professional.features", { returnObjects: true }),
      ),
      limitations: [],
      popular: true,
    },
    {
      name: "premium",
      displayName: t("plans.premium.name"),
      price: t("plans.premium.price"),
      period: t("plans.premium.period"),
      description: t("plans.premium.description"),
      features: ensureArray(
        t("plans.premium.features", { returnObjects: true }),
      ),
      limitations: [],
      popular: false,
    },
    {
      name: "enterprise",
      displayName: t("plans.enterprise.name"),
      price: t("plans.enterprise.price"),
      period: t("plans.enterprise.period"),
      description: t("plans.enterprise.description"),
      features: ensureArray(
        t("plans.enterprise.features", { returnObjects: true }),
      ),
      limitations: [],
      popular: false,
    },
  ];

  useEffect(() => {
    setCurrentPlan(subscriptionPlan || "free");
  }, [subscriptionPlan]);

  // Handle return from Stripe checkout
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const sessionId = urlParams.get("session_id");
    const status = urlParams.get("status");

    if (sessionId && status === "success") {
      // Verify the session and show success message
      verifySession(sessionId)
        .then((response) => {
          if (response.status === "success") {
            setSnackbar({
              open: true,
              message: response.message || t("notifications.paymentSuccess"),
              severity: "success",
            });
            // Refresh user data to get updated subscription plan
            window.location.reload();
          }
        })
        .catch((error) => {
          setSnackbar({
            open: true,
            message:
              error.message || t("notifications.paymentVerificationFailed"),
            severity: "warning",
          });
        });

      // Clean up URL parameters
      window.history.replaceState({}, document.title, window.location.pathname);
    } else if (status === "cancelled") {
      setSnackbar({
        open: true,
        message: t("notifications.paymentCancelled"),
        severity: "info",
      });

      // Clean up URL parameters
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, [t]);

  const handleUpgrade = (plan) => {
    setSelectedPlan(plan);
    setUpgradeDialog(true);
  };

  const confirmUpgrade = async () => {
    if (!selectedPlan) return;

    setLoading(true);
    try {
      // For paid plans, create checkout session and redirect to Stripe
      if (selectedPlan.name !== "free") {
        const response = await createCheckoutSession(selectedPlan.name);

        // Redirect to Stripe checkout
        window.location.href = response.checkout_url;
        return;
      }

      // For free plan (downgrade), handle directly
      const response = await changeSubscriptionPlan(selectedPlan.name);
      setCurrentPlan(selectedPlan.name);
      setSubscriptionPlan(selectedPlan.name);
      setSnackbar({
        open: true,
        message:
          response.message ||
          t("notifications.planChangeSuccess", {
            planName: selectedPlan.displayName,
          }),
        severity: "success",
      });
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.message || t("notifications.planChangeFailed"),
        severity: "error",
      });
    } finally {
      setLoading(false);
      setUpgradeDialog(false);
      setSelectedPlan(null);
    }
  };

  const handleDowngrade = (plan) => {
    setSelectedPlan(plan);
    setCancelDialog(true);
  };

  const confirmDowngrade = async () => {
    if (!selectedPlan) return;

    setLoading(true);
    try {
      const response = await changeSubscriptionPlan(selectedPlan.name);
      setCurrentPlan(selectedPlan.name);
      setSubscriptionPlan(selectedPlan.name);
      setSnackbar({
        open: true,
        message:
          response.message ||
          t("notifications.planChangeSuccess", {
            planName: selectedPlan.displayName,
          }),
        severity: "success",
      });
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.message || t("notifications.planChangeError"),
        severity: "error",
      });
    } finally {
      setLoading(false);
      setCancelDialog(false);
      setSelectedPlan(null);
    }
  };

  return (
    <Container>
      <Box>
        <PageHeader title={t("pageTitle")} />
        <Grid container spacing={3}>
          {plans.map((plan) => (
            <Grid size={{ xs: 12, md: 6 }} key={plan.name}>
              <Card
                sx={{
                  height: "100%",
                  position: "relative",
                  overflow: "visible",
                  border: plan.current ? 2 : 1,
                  borderColor: plan.current ? "primary.main" : "divider",
                  ...(plan.popular && {
                    zIndex: 1,
                  }),
                }}
              >
                {plan.popular && (
                  <Chip
                    label={t("badges.mostPopular")}
                    color="primary"
                    size="small"
                    sx={{
                      position: "absolute",
                      top: 0,
                      left: "50%",
                      transform: "translate(-50%, -50%)",
                      zIndex: 9,
                    }}
                  />
                )}
                <CardContent
                  sx={{
                    p: 3,
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <Box sx={{ textAlign: "center", mb: 3 }}>
                    <Typography
                      variant="h3"
                      sx={{ fontWeight: 600, mb: SPACING.xs }}
                    >
                      {plan.displayName}
                      {currentPlan === plan.name && (
                        <Chip
                          label={t("badges.currentPlan")}
                          color="primary"
                          size="small"
                          sx={{ ml: 1 }}
                        />
                      )}
                    </Typography>
                    <Typography
                      variant="bodySm"
                      color="text.secondary"
                      sx={{ mb: SPACING.sm }}
                    >
                      {plan.description}
                    </Typography>
                    <Typography
                      variant="h4"
                      sx={{ fontWeight: 600, mb: SPACING.xs }}
                    >
                      {plan.price}
                    </Typography>
                    <Typography variant="bodySm" color="text.secondary">
                      {plan.period}
                    </Typography>
                  </Box>

                  <List sx={{ flexGrow: 1, p: 0 }}>
                    {plan.features.map((feature, index) => (
                      <ListItem key={index} sx={{ px: 0, py: 0.5 }}>
                        <ListItemIcon sx={{ minWidth: 32 }}>
                          <Check color="#0bb050" />
                        </ListItemIcon>
                        <ListItemText
                          primary={<Typography>{feature}</Typography>}
                        />
                      </ListItem>
                    ))}
                    {plan.limitations.map((limitation, index) => (
                      <ListItem key={index} sx={{ px: 0, py: 0.5 }}>
                        <ListItemIcon sx={{ minWidth: 32 }}>
                          <X size={16} color="red" />
                        </ListItemIcon>
                        <ListItemText
                          primary={<Typography>{limitation}</Typography>}
                        />
                      </ListItem>
                    ))}
                  </List>

                  <Box sx={{ mt: SPACING.sm }}>
                    {currentPlan === plan.name ? (
                      <Button variant="outlined" fullWidth disabled>
                        {t("buttons.currentPlan")}
                      </Button>
                    ) : currentPlan === "free" && plan.name !== "free" ? (
                      <Button
                        variant="outlined"
                        fullWidth
                        startIcon={<ArrowUp size={16} />}
                        onClick={() => handleUpgrade(plan)}
                        disabled={loading}
                      >
                        {plan.name === "enterprise"
                          ? t("buttons.contactSales")
                          : t("buttons.upgrade")}
                      </Button>
                    ) : currentPlan !== "free" && plan.name === "free" ? (
                      <Button
                        variant="outlined"
                        color="error"
                        fullWidth
                        startIcon={<ArrowDown size={16} />}
                        onClick={() => handleDowngrade(plan)}
                        disabled={loading}
                      >
                        {t("buttons.downgradeToFree")}
                      </Button>
                    ) : currentPlan !== "free" &&
                      plan.name !== "free" &&
                      plan.name !== currentPlan ? (
                      <Button
                        variant="outlined"
                        fullWidth
                        startIcon={
                          getPlanLevel(plan.name) >
                          getPlanLevel(currentPlan) ? (
                            <ArrowUp size={16} />
                          ) : (
                            <ArrowDown size={16} />
                          )
                        }
                        onClick={() => handleUpgrade(plan)}
                        disabled={loading}
                      >
                        {getPlanLevel(plan.name) > getPlanLevel(currentPlan)
                          ? t("buttons.upgrade")
                          : t("buttons.changePlan")}
                      </Button>
                    ) : null}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* Upgrade Dialog */}
      <Dialog
        open={upgradeDialog}
        onClose={() => setUpgradeDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle variant="h4">
          {selectedPlan &&
            t("upgradeDialog.title", { planName: selectedPlan.displayName })}
        </DialogTitle>
        <DialogContent>
          {selectedPlan && (
            <Box>
              <Typography variant="bodyMd">
                <Trans
                  i18nKey="upgradeDialog.description"
                  ns="membership"
                  values={{
                    planName: selectedPlan.displayName,
                    price: selectedPlan.price,
                    period: selectedPlan.period,
                  }}
                  components={{ strong: <strong /> }}
                />
              </Typography>
              <Alert severity="info" sx={{ my: 2 }}>
                <Typography variant="bodySm">
                  {t("upgradeDialog.proRatedNotice")}
                </Typography>
              </Alert>
              <Typography
                variant="h3"
                sx={{ mt: 3, mb: SPACING.xs, fontWeight: 500 }}
              >
                {t("upgradeDialog.whatYouGetTitle")}
              </Typography>
              <List sx={{ p: 0 }}>
                {selectedPlan.features.slice(0, 4).map((feature, index) => (
                  <ListItem key={index} sx={{ px: 0, py: 0.5 }}>
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <Check color="#0bb050" />
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Typography variant="bodyMd">{feature}</Typography>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button variant="secondary" onClick={() => setUpgradeDialog(false)}>
            {t("upgradeDialog.cancel")}
          </Button>
          <Button
            onClick={confirmUpgrade}
            variant="contained"
            disabled={loading}
          >
            {loading ? t("status.processing") : t("status.confirmChange")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Downgrade Dialog */}
      <Dialog
        open={cancelDialog}
        onClose={() => setCancelDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{t("downgradeDialog.title")}</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: SPACING.sm }}>
            <Typography variant="bodySm">
              {t("downgradeDialog.warningMessage")}
            </Typography>
          </Alert>
          <Typography variant="bodySm">
            {t("downgradeDialog.description")}
          </Typography>
          <List sx={{ mt: 1 }}>
            {ensureArray(
              t("downgradeDialog.lostFeatures", { returnObjects: true }),
            ).map((feature, index) => (
              <ListItem key={index} sx={{ px: 0, py: 0.5 }}>
                <ListItemIcon sx={{ minWidth: 32 }}>
                  <X size={16} color="red" />
                </ListItemIcon>
                <ListItemText
                  primary={<Typography variant="bodySm">{feature}</Typography>}
                />
              </ListItem>
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelDialog(false)}>
            {t("buttons.keepCurrentPlan")}
          </Button>
          <Button
            onClick={confirmDowngrade}
            variant="contained"
            color="error"
            disabled={loading}
          >
            {loading ? t("status.processing") : t("buttons.downgradeToFree")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
}

export default MembershipPage;
